import React, { useEffect, useState, useRef } from "react";
import "./ChatBot.css";
import ItImage from "../../Assets/technical-support.png";
import HrImage from "../../Assets/team-building.png";
import ChatHomeImage from "../../Assets/chat-home.png";
import { FaRegStopCircle } from "react-icons/fa";
import * as apiClient from "../../ApiService";
import { v4 as uuidv4 } from "uuid";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

const chatCategory = [
  {
    id: "IT_SUPPORT",
    title: "IT Support",
    image: ItImage,
    // url: "https://n8n.srv896137.hstgr.cloud/webhook/4bc47dc3-bfa9-41a5-bc8d-657b4eff3d39", // TODO: add your IT webhook URL
    url: "https://n8n.srv896137.hstgr.cloud/webhook/bda81a92-a3ac-4743-a8b3-4ecee06b6741",
  },
  {
    id: "HR",
    title: "HR Support",
    image: HrImage,
    url: "https://n8n.srv896137.hstgr.cloud/webhook/4d247cfc-79d8-43bf-b87f-067067ea9a0e",
  },
];

const WELCOME_MESSAGE = "Hello! How can I help you today?";

export default function ChatBot({ openChatBot, handleChatBot }) {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typeWriterTimeoutRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const fullTextRef = useRef("");

  // 🔒 hard gate for auto-scroll (ref, not state, so effects can’t reset it)
  const autoScrollRef = useRef(true);

  const [messages, setMessages] = useState({});
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loadingResponse, setLoadingResponse] = useState(false);
  const [windowPosition, setWindowPosition] = useState({ x: null, y: null });
  const [typeWriterText, setTypeWriterText] = useState("");
  const [typeWriterActive, setTypeWriterActive] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  const typeWriterStoppedRef = useRef(false);

  function formatBotResponse(raw) {
    if (!raw) return "";

    let text = "";
    try {
      text = JSON.parse(`"${raw}"`);
    } catch (e) {
      text = raw.replace(/\\n/g, "\n").replace(/\\"/g, '"');
    }

    return text;
  }

  const handleQuestionClick = (isQuestion, textContent) => {
    if (isQuestion && textContent.trim()) {
      handleSend(textContent.trim());
    }
  };

  // ==========================
  // SEND MESSAGE + TYPEWRITER
  // ==========================
  const handleSend = (message) => {
    // when typewriter is going on then disable button is on and send new message won't work
    if (disableButton || loadingResponse) {
      return;
    }

    const textToSend = typeof message === "string" ? message : input;

    typeWriterStoppedRef.current = false;
    setTypeWriterText("");
    setTypeWriterActive(false);

    if (!textToSend.trim()) return;
    if (!selectedCategory) return;

    const categoryId = selectedCategory.id;
    const categoryUrl = selectedCategory.url;

    // append user message
    setMessages((prev) => ({
      ...prev,
      [categoryId]: [
        ...(prev[categoryId] || []),
        { sender: "user", text: textToSend.trim() },
      ],
    }));

    const payload = {
      sessionId,
      chatInput: textToSend.trim(),
    };

    // user is likely at bottom when sending, so allow auto-scroll again
    autoScrollRef.current = true;
    setUserHasScrolledUp(false);

    setLoadingResponse(true);
    apiClient
      .chatApi(categoryUrl, payload)
      .then((res) => {
        const fullText =
          res.response || "Temporary server issue. Please try again later.";
        fullTextRef.current = fullText;
        setTypeWriterText("");
        setTypeWriterActive(true);
        typeWriterStoppedRef.current = false;
        setDisableButton(true);

        let i = 0;

        function typeWriter() {
          if (typeWriterStoppedRef.current) {
            typeWriterTimeoutRef.current = null;
            return;
          }
          setTypeWriterText(fullText.slice(0, i));

          if (i < fullText.length) {
            i++;
            typeWriterTimeoutRef.current = setTimeout(typeWriter, 18);
          } else {
            // final bot message into correct category
            setMessages((prevMsgs) => ({
              ...prevMsgs,
              [categoryId]: [
                ...(prevMsgs[categoryId] || []),
                { sender: "bot", text: fullText },
              ],
            }));
            setTypeWriterActive(false);
            setDisableButton(false);
            typeWriterTimeoutRef.current = null;
            fullTextRef.current = "";
          }
        }

        typeWriter();
      })
      .catch((err) => {
        window.alert("Error:", err.message);
      })
      .finally(() => setLoadingResponse(false));

    setInput("");
  };

  // ===============
  // DRAG HANDLERS
  // ===============
  const handleDragStart = (e) => {
    setDragging(true);
    const rect = e.target.closest(".chatbot-window").getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    document.body.style.userSelect = "none";
  };

  const handleDrag = (e) => {
    if (!dragging) return;
    setWindowPosition({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
    });
  };

  const handleDragEnd = () => {
    setDragging(false);
    document.body.style.userSelect = "";
  };

  // =================
  // AUTO-SCROLL LOGIC
  // =================
  const messageAutoScroll = () => {
    if (!autoScrollRef.current) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleStopTypeWriter = () => {
    if (typeWriterTimeoutRef.current) {
      clearTimeout(typeWriterTimeoutRef.current);
      typeWriterTimeoutRef.current = null;
    }
    // When stopping, immediately show the full text.
    const categoryId = selectedCategory.id;

    const fullText = fullTextRef.current;

    // Only add if there's actual text
    if (fullText) {
      setMessages((prevMsgs) => ({
        ...prevMsgs,
        [categoryId]: [
          ...(prevMsgs[categoryId] || []),
          { sender: "bot", text: fullText },
        ],
      }));
    }

    typeWriterStoppedRef.current = true;
    setTypeWriterActive(false);
    setDisableButton(false);
    setTypeWriterText("");
    fullTextRef.current = "";
  };

  // ==============================
  // OPEN/CLOSE & SESSION HANDLING
  // ==============================
  useEffect(() => {
    if (openChatBot) {
      setSessionId(uuidv4());
      setInput("");
      setWindowPosition({ x: null, y: null });
      // when reopening, allow auto-scroll again
      autoScrollRef.current = true;
      setUserHasScrolledUp(false);
    } else {
      setSessionId(null);
    }
    setOpen(openChatBot);
    if (!openChatBot) {
      setSelectedCategory(null);
    }
  }, [openChatBot]);

  // ==========================
  // AUTO-SCROLL ON NEW EVENTS
  // ==========================
  useEffect(() => {
    messageAutoScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typeWriterText, selectedCategory]);

  // ======================
  // DRAG LISTENERS
  // ======================
  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("mouseup", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDrag);
      window.removeEventListener("mouseup", handleDragEnd);
    };
  }, [dragging]);

  // ==========================================
  // STOP AUTO-SCROLL WHEN USER INTERACTS
  // ==========================================
  useEffect(() => {
    const chatContainer = messagesContainerRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const atBottom = scrollHeight - scrollTop - clientHeight < 5;

      setUserHasScrolledUp(!atBottom);

      if (!atBottom) {
        // user scrolled away from bottom -> lock auto-scroll OFF
        autoScrollRef.current = false;
      } else {
        // user manually came back to bottom -> allow auto-scroll again
        autoScrollRef.current = true;
      }
    };

    const handleUserInteraction = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const atBottom = scrollHeight - scrollTop - clientHeight < 5;

      if (!atBottom) {
        autoScrollRef.current = false;
        setUserHasScrolledUp(true);
      }
    };

    chatContainer.addEventListener("scroll", handleScroll);
    chatContainer.addEventListener("wheel", handleUserInteraction);
    chatContainer.addEventListener("mousedown", handleUserInteraction);
    chatContainer.addEventListener("touchstart", handleUserInteraction);

    return () => {
      chatContainer.removeEventListener("scroll", handleScroll);
      chatContainer.removeEventListener("wheel", handleUserInteraction);
      chatContainer.removeEventListener("mousedown", handleUserInteraction);
      chatContainer.removeEventListener("touchstart", handleUserInteraction);
    };
  }, []);

  // ======================
  // CATEGORY SELECT
  // ======================
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);

    setMessages((prev) => {
      if (!prev[category.id]) {
        return {
          ...prev,
          [category.id]: [{ sender: "bot", text: WELCOME_MESSAGE }],
        };
      }
      return prev;
    });

    // on category change, reset scroll to bottom behavior
    autoScrollRef.current = true;
    setUserHasScrolledUp(false);
  };

  const currentMessages =
    selectedCategory && messages[selectedCategory.id]
      ? messages[selectedCategory.id]
      : [];

  useEffect(() => {
    if (currentMessages.length > 0) {
    }
  }, [currentMessages]);

  return (
    <div
      className={`chatbot-container ${open ? "open" : ""}`}
      style={{
        position: "fixed",
        left: windowPosition.x,
        top: windowPosition.y,
        zIndex: 1000,
        transition: dragging ? "none" : "all box-shadow 0.3s ease",
      }}
    >
      {!open && (
        <button
          className="chatbot-toggle"
          onClick={() => handleChatBot(true)}
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 1000,
            transition: dragging ? "none" : "all box-shadow 0.3s ease",
          }}
        >
          💬 Chat
        </button>
      )}

      {open && (
        <div className="chatbot-window">
          <div
            className="chatbot-header"
            style={{ cursor: "move" }}
            onMouseDown={handleDragStart}
          >
            <span>Sampurna</span>
            <button
              className="chatbot-close"
              onClick={() => handleChatBot(false)}
            >
              ×
            </button>
          </div>
          <iframe
            src="https://assist.sampurna.cloud/"
            title="Sampurna"
            style={{ width: "100%", height: "calc(100% - 50px)", border: "none" }}
            allow="microphone 'src'; clipboard-write 'src'; camera 'src'"
          />
        </div>
      )}
    </div>
  );
}
